import { motion } from "framer-motion";
import { Monitor, Compass, Cpu, Radio, AlertTriangle, Navigation, Sliders, Eye, Wifi, BarChart3 } from "lucide-react";

const licenseFeatures = [
  { icon: Monitor, title: "General System Interactions", desc: "General system interactions and status updates." },
  { icon: Eye, title: "Primary Flight Display", desc: "PFD operations: attitude, airspeed, and altitude data." },
  { icon: Compass, title: "Multi-Function Display", desc: "Navigation maps, engine monitoring, and system synoptics." },
  { icon: Sliders, title: "Reversionary Display", desc: "Reversionary Flight Display operations for backup mode." },
  { icon: Radio, title: "Audio Panel Controls", desc: "Communication and navigation audio management." },
  { icon: Cpu, title: "Autopilot Flight Control", desc: "AFCS interactions: mode selections and trims." },
  { icon: Navigation, title: "Keypad Inputs", desc: "Keypad inputs for data entry and mode changes." },
  { icon: AlertTriangle, title: "System Failure Simulation", desc: "Engine, navigation, or autopilot malfunction scenarios." },
];

const g1000Features = [
  { icon: Eye, title: "Synthetic Vision", desc: "Integrated PFD and MFD with synthetic vision (terrain and glide path) and traffic information." },
  { icon: Navigation, title: "LNAV & VNAV", desc: "LNAV and VNAV functionality, including 3D approach capabilities for realistic IFR navigation." },
  { icon: Cpu, title: "Full AFCS", desc: "Full AFCS functionality with mode control and failure simulation." },
  { icon: Radio, title: "GPS / VOR / ILS", desc: "GPS, VOR, ILS, and COMM/NAV radio tuning with realistic keypad and audio panel interactions." },
  { icon: BarChart3, title: "Engine Indication System", desc: "Engine Indication System for fuel, oil, and electrical monitoring." },
  { icon: Sliders, title: "Reversionary Mode", desc: "Reversionary Mode for emergency backup display." },
  { icon: Wifi, title: "Advanced Failure Injection", desc: "Advanced failure injection for sensors, servos, and communication systems." },
];

export function FeaturesSection() {
  return (
    <section id="features" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 280, damping: 32 }} className="mb-12">
          <p className="text-xs font-mono tracking-[0.25em] uppercase font-bold mb-2" style={{ color: "oklch(0.55 0.22 25)" }}>— G1000NXi Features —</p>
          <h2 className="text-4xl md:text-5xl font-black text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Professional License <span style={{ color: "oklch(0.28 0.09 240)" }}>Features</span>
          </h2>
        </motion.div>

        {/* License features */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {licenseFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06, type: "spring", stiffness: 280, damping: 32 }}
                className="bg-white rounded-xl border border-border p-5 hover:shadow-[0_4px_16px_oklch(0.12_0.03_240/0.08)] transition-shadow"
                style={{ borderTop: "3px solid oklch(0.28 0.09 240)" }}>
                <div className="w-9 h-9 rounded-lg bg-primary/8 border border-primary/15 flex items-center justify-center mb-3">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-bold text-foreground text-sm mb-1.5">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* G1000NXi specific */}
        <div className="bg-primary rounded-2xl p-8">
          <p className="text-xs font-mono tracking-[0.25em] uppercase font-bold mb-2 text-white/50">— G1000NXi-Specific Enhancements —</p>
          <h3 className="text-2xl font-black text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Advanced Simulation Capabilities
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {g1000Features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.07, type: "spring", stiffness: 280, damping: 32 }}
                  className="bg-white/8 rounded-xl border border-white/10 p-5 hover:bg-white/12 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mb-3">
                    <Icon className="w-4 h-4 text-white/80" />
                  </div>
                  <h4 className="font-bold text-white text-sm mb-1">{f.title}</h4>
                  <p className="text-xs text-white/55 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
